import { useState, useEffect, useRef, useCallback } from "react";
import { X, Play, Pause, SkipBack, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default icon leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const API_JAVA = process.env.REACT_APP_VILOX_FLOTA_API || "http://localhost:8080";
const SPEEDS = [1, 5, 10, 30];

const iconVehiculo = new L.DivIcon({
  className: "",
  html: `<div style="width:14px;height:14px;background:#3B82F6;border:2px solid #1D4ED8;border-radius:50%;box-shadow:0 0 4px rgba(0,0,0,0.4)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const iconOrigen = new L.DivIcon({
  className: "",
  html: `<div style="width:14px;height:14px;background:#22C55E;border:2px solid #15803D;border-radius:50%"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const iconDestino = new L.DivIcon({
  className: "",
  html: `<div style="width:14px;height:14px;background:#EF4444;border:2px solid #B91C1C;border-radius:50%"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Componente interno que mueve el mapa al punto actual
function MapController({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.panTo(position, { animate: true, duration: 0.3 });
  }, [position, map]);
  return null;
}

export default function ReplayViaje({ viaje, onClose }) {
  const intervalRef = useRef(null);

  const [tracks, setTracks] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar tracks del viaje
  useEffect(() => {
    const fetchTracks = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_JAVA}/api/replay/${viaje.id}`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        if (data.length === 0) {
          setError("No hay tracks GPS para este viaje.");
        } else {
          setTracks(data);
        }
      } catch (e) {
        setError("No se pudieron cargar los tracks: " + e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTracks();
  }, [viaje.id]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!playing || tracks.length === 0) {
      stopInterval();
      return;
    }
    intervalRef.current = setInterval(() => {
      setIndex((prev) => {
        if (prev >= tracks.length - 1) {
          setPlaying(false);
          setFinished(true);
          stopInterval();
          return prev;
        }
        return prev + 1;
      });
    }, Math.max(50, 500 / speed));

    return stopInterval;
  }, [playing, speed, tracks.length, stopInterval]);

  useEffect(() => () => stopInterval(), [stopInterval]);

  const handleReset = () => {
    setPlaying(false);
    setFinished(false);
    setIndex(0);
  };

  const currentTrack = tracks[index];
  const currentPos = currentTrack ? [currentTrack.latitude, currentTrack.longitude] : null;

  // Origen: primer tramo, establecimientoOrigen
  const primerTramo = viaje.tramos?.[0];
  const ultimoTramo = viaje.tramos?.[viaje.tramos.length - 1];
  const origenEst = primerTramo?.establecimientoOrigen;
  const destinoEst = ultimoTramo?.establecimientoDestino;

  const originPos = origenEst?.latitud && origenEst?.longitud
    ? [parseFloat(origenEst.latitud), parseFloat(origenEst.longitud)]
    : tracks.length > 0 ? [tracks[0].latitude, tracks[0].longitude] : null;

  const destPos = destinoEst?.latitud && destinoEst?.longitud
    ? [parseFloat(destinoEst.latitud), parseFloat(destinoEst.longitud)]
    : tracks.length > 0 ? [tracks[tracks.length - 1].latitude, tracks[tracks.length - 1].longitude] : null;
  const polylinePoints = tracks.map((t) => [t.latitude, t.longitude]);
  const traveledPoints = tracks.slice(0, index + 1).map((t) => [t.latitude, t.longitude]);

  const formatTime = (gpstime) => {
    if (!gpstime) return "--:--";
    return new Date(gpstime * 1000).toLocaleTimeString("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calcDistancia = () => {
    if (index < 1) return "0.00";
    let km = 0;
    for (let i = 1; i <= index; i++) {
      const a = tracks[i - 1];
      const b = tracks[i];
      const R = 6371;
      const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
      const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
      const x =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((a.latitude * Math.PI) / 180) *
          Math.cos((b.latitude * Math.PI) / 180) *
          Math.sin(dLon / 2) ** 2;
      km += R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    }
    return km.toFixed(2);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-800 text-white">
          <div className="flex items-center gap-2">
            <Navigation className="h-4 w-4 text-blue-400" />
            <span className="font-semibold text-sm">
              Replay — {viaje.codigoViaje || viaje.id}
            </span>
            <span className="text-xs text-gray-400 ml-2">{viaje.estado}</span>
          </div>
          <button onClick={onClose} className="hover:text-red-400 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mapa */}
        <div className="relative" style={{ height: 380 }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <p className="text-gray-500 text-sm">Cargando tracks GPS...</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}
          {!loading && !error && originPos && (
            <MapContainer
              center={originPos}
              zoom={10}
              style={{ width: "100%", height: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Ruta completa en gris */}
              <Polyline positions={polylinePoints} color="#9CA3AF" weight={2} opacity={0.5} />

              {/* Ruta recorrida en azul */}
              <Polyline positions={traveledPoints} color="#3B82F6" weight={3} opacity={0.8} />

              {/* Marcador origen */}
              <Marker position={originPos} icon={iconOrigen} />

              {/* Marcador destino */}
              <Marker position={destPos} icon={iconDestino} />

              {/* Marcador vehículo */}
              {currentPos && <Marker position={currentPos} icon={iconVehiculo} />}

              {/* Controlador de cámara */}
              {currentPos && <MapController position={currentPos} />}
            </MapContainer>
          )}
        </div>

        {/* Controles */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200">
          {/* Timeline */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500 w-12 text-right">
              {formatTime(tracks[0]?.gpstime)}
            </span>
            <input
              type="range"
              min={0}
              max={tracks.length > 1 ? tracks.length - 1 : 0}
              value={index}
              onChange={(e) => {
                setPlaying(false);
                setFinished(false);
                setIndex(Number(e.target.value));
              }}
              className="flex-1 h-2 accent-blue-600"
              disabled={tracks.length === 0}
            />
            <span className="text-xs text-gray-500 w-12">
              {formatTime(tracks[tracks.length - 1]?.gpstime)}
            </span>
          </div>

          {/* Botones y stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                disabled={tracks.length === 0}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-40 transition-colors"
                title="Reiniciar"
              >
                <SkipBack className="h-4 w-4 text-gray-700" />
              </button>

              {finished ? (
                <span className="px-3 py-2 text-xs rounded-lg bg-green-100 text-green-700 font-medium">
                  Completado
                </span>
              ) : (
                <button
                  onClick={() => setPlaying((p) => !p)}
                  disabled={tracks.length === 0}
                  className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  title={playing ? "Pausar" : "Reproducir"}
                >
                  {playing ? (
                    <Pause className="h-4 w-4 text-white" />
                  ) : (
                    <Play className="h-4 w-4 text-white" />
                  )}
                </button>
              )}

              <div className="flex gap-1 ml-2">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSpeed(s)}
                    className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                      speed === s
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-4 text-xs text-gray-600">
              <span>
                ⚡ <strong>{currentTrack?.speed?.toFixed(0) ?? "--"}</strong> km/h
              </span>
              <span>
                📏 <strong>{calcDistancia()}</strong> km
              </span>
              <span>
                🕐 <strong>{formatTime(currentTrack?.gpstime)}</strong>
              </span>
              <span className="text-gray-400">
                {index + 1}/{tracks.length} pts
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
